import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import {
  getFaxSettings,
  updateFaxSettings,
  type IFaxSettings,
} from '@services/api/fax/settings.api'
import { getClinicNumbers } from '@services/api/phoneManagement.api'
import { releaseFaxNumber } from '@services/api/fax/numbers.api'
import { IClinicNumber } from '@dataTypes/clinic.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useAppSelector } from '@store/hooks'
import { areValuesEqual } from '@utils/settings.util'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { FilterSelection } from '@enamelai/design-system'

const LOADING_CONTAINER = 'settings-fax'

export type FaxSettingsForm = Pick<
  IFaxSettings,
  | 'add_clinic_logo'
  | 'show_unread'
  | 'notify_on_new_fax'
  | 'notify_on_failed_delivery'
  | 'resend_failed_fax'
  | 'retention_period'
  | 'retention_period_unit'
  | 'max_pages'
  | 'show_images'
>

const DEFAULT_SETTINGS: FaxSettingsForm = {
  add_clinic_logo: false,
  show_unread: true,
  notify_on_new_fax: true,
  notify_on_failed_delivery: true,
  resend_failed_fax: false,
  retention_period: 0,
  retention_period_unit: 'No expiration',
  max_pages: null,
  show_images: 'ask',
}

export interface FaxNumberRow {
  clinic_number_id?: string
  name: string
  fax_number: string
  assigned_to: string
  status: string
  is_ported: boolean
}

const isFaxCapable = (n: IClinicNumber): boolean => {
  if (!n.contact_methods) return false
  try {
    const methods = JSON.parse(n.contact_methods) as unknown
    return Array.isArray(methods) && methods.includes('fax')
  } catch {
    return false
  }
}

const dtoToForm = (dto: IFaxSettings): FaxSettingsForm => ({
  add_clinic_logo: Boolean(dto.add_clinic_logo),
  show_unread: Boolean(dto.show_unread),
  notify_on_new_fax: Boolean(dto.notify_on_new_fax),
  notify_on_failed_delivery: Boolean(dto.notify_on_failed_delivery),
  resend_failed_fax: Boolean(dto.resend_failed_fax),
  retention_period: Number(dto.retention_period ?? 0),
  retention_period_unit: dto.retention_period_unit ?? 'No expiration',
  max_pages:
    dto.max_pages === null || dto.max_pages === undefined
      ? null
      : Number(dto.max_pages),
  show_images: dto.show_images ?? 'ask',
})

export interface IFaxSettingsHooks {
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  clinicId: string
  loading: boolean
  dataReady: boolean
  data: FaxSettingsForm
  savedBaseline: FaxSettingsForm
  handleChange: (
    key: keyof FaxSettingsForm,
    value: boolean | number | string | null,
  ) => void
  handleReset: () => void
  handleSave: () => Promise<void>
  hasChanges: boolean
  mainSearchQuery: string
  setMainSearchQuery: Dispatch<SetStateAction<string>>
  mainFilters: FilterSelection
  setMainFilters: Dispatch<SetStateAction<FilterSelection>>
  statusFilterChoices: string[]
  gridRows: FaxNumberRow[]
  faxNumbersCount: number
  numbersLoading: boolean
  refreshNumbers: () => Promise<void>
  removeFaxNumber: (clinicNumberId: string) => Promise<void>
  addMethod: string | null
  setAddMethod: Dispatch<SetStateAction<string | null>>
}

const useFaxSettings = (): IFaxSettingsHooks => {
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const selectedClinicId = useAppSelector(
    (s) => s.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')

  const [savedBaseline, setSavedBaseline] =
    useState<FaxSettingsForm>(DEFAULT_SETTINGS)
  const [data, setData] = useState<FaxSettingsForm>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(false)
  const [mainSearchQuery, setMainSearchQuery] = useState('')
  const [mainFilters, setMainFilters] = useState<FilterSelection>(() => ({
    status: [],
  }))
  const [addMethod, setAddMethod] = useState<string | null>(null)
  const [faxNumbers, setFaxNumbers] = useState<IClinicNumber[]>([])
  const [numbersLoading, setNumbersLoading] = useState(false)
  const [settingsReady, setSettingsReady] = useState(false)
  const [numbersReady, setNumbersReady] = useState(false)
  const settingsGen = useRef(0)
  const numbersGen = useRef(0)

  useClinicChange(clinicId, () => {
    settingsGen.current += 1
    numbersGen.current += 1
    setSettingsReady(false)
    setNumbersReady(false)
    setLoading(true)
  })
  const dataReady = settingsReady && numbersReady

  useSettingsSubpageLoading(!dataReady)

  const hasChanges = useMemo(
    () =>
      !areValuesEqual(
        [
          data.add_clinic_logo,
          data.show_unread,
          data.notify_on_new_fax,
          data.notify_on_failed_delivery,
          data.resend_failed_fax,
          data.retention_period,
          data.retention_period_unit,
          data.max_pages ?? -1,
          data.show_images,
        ],
        [
          savedBaseline.add_clinic_logo,
          savedBaseline.show_unread,
          savedBaseline.notify_on_new_fax,
          savedBaseline.notify_on_failed_delivery,
          savedBaseline.resend_failed_fax,
          savedBaseline.retention_period,
          savedBaseline.retention_period_unit,
          savedBaseline.max_pages ?? -1,
          savedBaseline.show_images,
        ],
      ),
    [data, savedBaseline],
  )

  const loadSettings = useCallback(async () => {
    const gen = ++settingsGen.current
    if (!clinicId) {
      setLoading(false)
      setSettingsReady(true)
      return
    }
    setLoading(true)
    try {
      const res = await getFaxSettings()
      const s = res.data?.fax_settings
      if (res.statusCode !== 200 || !s) {
        showToast(
          createFailedToast('Fax settings', res.message || 'Failed to load.'),
        )
        return
      }
      const mapped = dtoToForm(s)
      setSavedBaseline(mapped)
      setData(mapped)
    } catch (e) {
      showToast(createFailedToast('Fax settings', 'Could not load settings.'))
    } finally {
      if (settingsGen.current === gen) {
        setLoading(false)
        setSettingsReady(true)
      }
    }
  }, [clinicId, showToast])

  useEffect(() => {
    void loadSettings()
  }, [loadSettings])

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const handleChange = useCallback(
    (
      key: keyof FaxSettingsForm,
      value: boolean | number | string | null,
    ): void => {
      setData((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const handleReset = useCallback((): void => {
    setData({ ...savedBaseline })
  }, [savedBaseline])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      showToast(createFailedToast('Fax settings', 'No clinic selected.'))
      return
    }
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const res = await updateFaxSettings({ ...data })
      const s = res.data?.fax_settings
      if (res.statusCode !== 200 || !s) {
        showToast(
          createFailedToast('Fax settings', res.message || 'Save failed.'),
        )
        return
      }
      const mapped = dtoToForm(s)
      setSavedBaseline(mapped)
      setData(mapped)
      showToast(createSuccessToast('Saved', 'Fax settings saved.'))
    } catch (e) {
      showToast(createFailedToast('Fax settings', 'Save failed.'))
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, data, hideLoading, showLoading, showToast])

  const refreshNumbers = useCallback(async (): Promise<void> => {
    const gen = ++numbersGen.current
    if (!clinicId) {
      setNumbersReady(true)
      return
    }
    setNumbersLoading(true)
    try {
      const res = await getClinicNumbers()
      const numbers = res.data?.clinic_numbers ?? []
      setFaxNumbers(numbers.filter(isFaxCapable))
    } catch (e) {
      showToast(createFailedToast('Fax numbers', 'Could not load fax numbers.'))
    } finally {
      if (numbersGen.current === gen) {
        setNumbersLoading(false)
        setNumbersReady(true)
      }
    }
  }, [clinicId, showToast])

  useEffect(() => {
    void refreshNumbers()
  }, [refreshNumbers])

  const removeFaxNumber = useCallback(
    async (clinicNumberId: string): Promise<void> => {
      const target = faxNumbers.find(
        (n) => n.clinic_number_id === clinicNumberId,
      )
      if (!target) return
      if (target.status === 'Ported In') {
        showToast(
          createFailedToast(
            'Fax numbers',
            'Please contact EnamelAI to port out this number.',
          ),
        )
        return
      }
      setNumbersLoading(true)
      try {
        const res = await releaseFaxNumber({
          clinic_number_id: clinicNumberId,
          phone_number: target.phone_number,
          sinch_service_id: target.sinch_service_id ?? null,
        })
        if (res.statusCode !== 200) {
          showToast(
            createFailedToast(
              'Fax numbers',
              res.message || 'Failed to remove.',
            ),
          )
          return
        }
        showToast(createSuccessToast('Removed', 'Fax number released.'))
        await refreshNumbers()
      } catch (e) {
        showToast(createFailedToast('Fax numbers', 'Failed to remove number.'))
      } finally {
        setNumbersLoading(false)
      }
    },
    [faxNumbers, refreshNumbers, showToast],
  )

  const statusFilterChoices = useMemo<string[]>(
    () => Array.from(new Set(faxNumbers.map((n) => n.status).filter(Boolean))),
    [faxNumbers],
  )

  const gridRows = useMemo<FaxNumberRow[]>(() => {
    const query = mainSearchQuery.trim().toLowerCase()
    const statusSelection = mainFilters.status ?? []
    return faxNumbers
      .filter((n) => {
        if (!query) return true
        return (
          (n.phone_number ?? '').toLowerCase().includes(query) ||
          (n.line_name ?? '').toLowerCase().includes(query)
        )
      })
      .filter(
        (n) =>
          statusSelection.length === 0 || statusSelection.includes(n.status),
      )
      .map((n) => ({
        clinic_number_id: n.clinic_number_id,
        name: n.line_name || 'Fax line',
        fax_number: n.phone_number,
        assigned_to: n.routed_to_value || (n.is_main ? 'Main line' : 'Clinic'),
        status: n.status,
        is_ported: n.status === 'Ported In',
      }))
  }, [faxNumbers, mainSearchQuery, mainFilters])

  return {
    setHeaderButtons,
    setHeaderTitle,
    clinicId,
    loading,
    dataReady,
    data,
    savedBaseline,
    handleChange,
    handleReset,
    handleSave,
    hasChanges,
    mainSearchQuery,
    setMainSearchQuery,
    mainFilters,
    setMainFilters,
    statusFilterChoices,
    gridRows,
    faxNumbersCount: faxNumbers.length,
    numbersLoading,
    refreshNumbers,
    removeFaxNumber,
    addMethod,
    setAddMethod,
  }
}

export default useFaxSettings
